import { useCallback } from 'react'

import type { AccountRow } from '@/api/types'
import { formatMoney, formatSignedMoney } from '@/lib/format'

import styles from './Table.module.css'

function PctUsed({ pct, pacePct }: { pct: number | null; pacePct: number }) {
  if (pct === null) return <span className="text-faint">no budget</span>
  const width = Math.max(0, Math.min(100, pct))
  return (
    <div className={styles.pctCell}>
      <span className="num">{Math.round(pct)}%</span>
      <svg className={styles.pctTrack} aria-hidden="true" focusable="false">
        <rect
          className={pct <= pacePct ? styles.fillGood : styles.fillBad}
          x="0"
          y="0"
          width={`${width}%`}
          height="100%"
          rx="2"
        />
      </svg>
    </div>
  )
}

function AccountTableRow({
  row,
  pacePct,
  onActivate,
}: {
  row: AccountRow
  pacePct: number
  onActivate: (account: string) => void
}) {
  const handleClick = useCallback(() => onActivate(row.account), [onActivate, row.account])
  return (
    <tr className={styles.clickable} onClick={handleClick}>
      <td className={`${styles.left} ${styles.code}`}>{row.account}</td>
      <td className={`${styles.left} ${styles.title}`}>
        <button
          type="button"
          className={styles.cellButton}
          aria-label={`${row.account} ${row.title}: view transactions`}
        >
          {row.title}
        </button>
      </td>
      <td className="num">{formatMoney(row.budget)}</td>
      <td className="num">{formatMoney(row.actual)}</td>
      <td className={`num ${row.variance > 0 ? styles.varPos : styles.varNeg}`}>{formatSignedMoney(row.variance)}</td>
      <td>
        <PctUsed pct={row.pct_used} pacePct={pacePct} />
      </td>
    </tr>
  )
}

interface AccountTableProps {
  accounts: AccountRow[]
  pacePct: number
  quarterShort: string
  onActivate: (account: string) => void
}

export function AccountTable({ accounts, pacePct, quarterShort, onActivate }: AccountTableProps) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.left} scope="col">
            Acct
          </th>
          <th className={styles.left} scope="col">
            Line item
          </th>
          <th scope="col">{quarterShort} budget</th>
          <th scope="col">QTD actual</th>
          <th scope="col">Variance</th>
          <th scope="col">% of {quarterShort} used</th>
        </tr>
      </thead>
      <tbody>
        {accounts.map((row) => (
          <AccountTableRow key={row.account} row={row} pacePct={pacePct} onActivate={onActivate} />
        ))}
      </tbody>
    </table>
  )
}
